import React from 'react';
import * as T from '../time.js';
import { api } from '../api.js';
import { Icon, Lbl } from './components.jsx';
import { Button } from './ds/fgcp-ds.jsx';

const { useState: uF, useEffect: uFE, useRef: uFR } = React;

const ICON_BY_TYPE = (name = '', type = '') => {
  const ext = name.toLowerCase().split('.').pop() ?? '';
  if (type.startsWith('image/')) return 'image';
  if (ext === 'pdf') return 'file-text';
  if (['xls', 'xlsx', 'csv'].includes(ext)) return 'table';
  if (['zip', '7z', 'tar', 'gz'].includes(ext)) return 'package';
  return 'paperclip';
};

const fmtSize = (n) => {
  const b = Number(n) || 0;
  if (b < 1024) return b + 'B';
  if (b < 1024 * 1024) return (b / 1024).toFixed(0) + 'KB';
  return (b / 1024 / 1024).toFixed(1) + 'MB';
};

const row = {
  display: 'flex', alignItems: 'center', gap: 9, padding: '7px 9px',
  border: '1px solid var(--line)', borderRadius: 'var(--r-sm)', background: 'var(--surface-2)',
};

/** 짧은 만료의 링크를 받아 내려받는다 */
const downloadFile = id => api.get(`/api/v1/files/${id}/download-url`).then(r => { window.location.href = r.url; });

/**
 * 파일 하나를 올린다 — 업로드 URL 발급 → 저장소로 직접 → 서버가 저장소에 확인. 올린 파일 {id, original_name, content_type, size_bytes}.
 * 어디에 붙이는지는 부르는 쪽이 저장 요청(fileIds·newVersions)으로 정한다
 */
export async function uploadFile(file, scope) {
  const issued = await api.post('/api/v1/files/upload-url', { fileName: file.name, contentType: file.type || 'application/octet-stream', size: file.size, scope });
  const put = await fetch(issued.uploadUrl, { method: 'PUT', headers: { 'content-type': file.type || 'application/octet-stream' }, body: file });
  if (!put.ok) throw new Error(`업로드 실패 (${put.status})`);
  await api.post(`/api/v1/files/${issued.fileId}/confirm`, {});
  return { id: issued.fileId, original_name: file.name, content_type: file.type, size_bytes: file.size };
}

/** 첨부 한 줄 — 클릭하면 내려받는다. 문서함 새 판(2-4c)이면 판 번호와 이전 판들 */
function FileRow({ file, onRemove, busy }) {
  const [downloading, setDownloading] = uF(false);
  const [open, setOpen] = uF(false);
  const download = () => {
    setDownloading(true);
    downloadFile(file.id).catch(() => {}).finally(() => setDownloading(false));
  };
  const older = file.versions ?? [];
  return (
    <div data-file={file.id} data-version={file.version}>
    <div style={row}>
      <Icon name={ICON_BY_TYPE(file.original_name, file.content_type)} size={14} color="var(--ink-3)" />
      <button onClick={download} disabled={downloading} title="내려받기"
        style={{ flex: 1, minWidth: 0, textAlign: 'left', fontSize: 12.5, fontWeight: 500, color: 'var(--info)' }}>
        <span style={{ display: 'block', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {file.original_name}
        </span>
      </button>
      {file.version > 1 && <span className="mono" style={{ fontSize: 10.5, color: 'var(--acc)', whiteSpace: 'nowrap' }}>{file.version}판</span>}
      {older.length > 0 && <button className="link" style={{ fontSize: 11 }} onClick={() => setOpen(o => !o)} data-older-versions={older.length}>이전 판 {older.length}</button>}
      <span className="mono" style={{ fontSize: 10.5, color: 'var(--ink-3)', whiteSpace: 'nowrap' }}>
        {fmtSize(file.size_bytes)}
      </span>
      {onRemove && (
        <button onClick={() => onRemove(file)} disabled={busy} aria-label="첨부 제외"
          style={{ display: 'inline-flex', color: 'var(--ink-3)' }}>
          <Icon name="x" size={12} />
        </button>
      )}
    </div>
    {open && (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 3, padding: '4px 0 2px 28px' }} data-older-list>
        {older.map(v => (
          <button key={v.id} className="link" style={{ fontSize: 11.5, textAlign: 'left' }} onClick={() => downloadFile(v.id).catch(() => {})} data-older={v.version}>
            {v.version}판 · {v.original_name} · {fmtSize(v.size_bytes)} · {T.ymd(v.created_at)}
          </button>))}
      </div>
    )}
    </div>
  );
}

/**
 * 첨부 영역.
 * - target을 주면 그 업무 객체의 첨부를 서버에서 불러와 보여준다(읽기 전용).
 * - target 없이 쓰면(결재 기안·초안 고치기, 글 쓰기·고치기) 올리고 뺀 목록을 부모에게 알려주고, 저장할 때 부모가 그 목록(fileIds)으로 보낸다.
 *   결재 문서·게시글의 첨부는 그 저장 요청으로만 바뀐다(서버가 한 트랜잭션에서 — 계획 문서 2-1b·2-4b). initialFiles는 이미 붙어 있던 파일이다.
 * - 업로드는 앱을 거치지 않고 저장소로 직접 올라간다.
 */
export function Attachments({ target, scope = 'boards', canUpload = false, onChange, label = '첨부파일', initialFiles }) {
  const [files, setFiles] = uF(initialFiles ?? []);
  // 업무 객체(target)에 바로 붙이는 길은 없다 — 올리기는 저장 요청에 실을 목록을 만드는 화면(target 없이)에서만
  const uploadable = canUpload && !target;
  const [busy, setBusy] = uF(false);
  const [error, setError] = uF('');
  const inputRef = uFR(null);

  const query = target?.postId ? `postId=${target.postId}`
    : target?.approvalDocumentId ? `approvalDocumentId=${target.approvalDocumentId}`
    : target?.taskId ? `taskId=${target.taskId}` : null;

  uFE(() => {
    if (!query) return;
    api.get(`/api/v1/files?${query}`).then(r => setFiles(r.files)).catch(() => {});
  }, [query]);

  const pick = () => inputRef.current?.click();

  const upload = async (file) => {
    setBusy(true); setError('');
    try {
      // 업로드 URL 발급 → 저장소로 직접 → 서버가 저장소에 확인
      const up = await uploadFile(file, scope);
      // 4) 부모에게 알린다 — 저장할 때 그 목록(fileIds)으로 붙는다. 여러 개를 연속으로 올릴 때 이전 목록을 덮어쓰지 않도록 갱신 함수를 쓴다
      setFiles(prev => {
        const added = [...prev, up];
        onChange?.(added);
        return added;
      });
    } catch (e) {
      setError(e?.message ?? '업로드에 실패했습니다');
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  const onPicked = (e) => {
    const list = Array.from(e.target.files ?? []);
    // 여러 개를 고르면 순서대로 올린다(동시 업로드로 서버를 몰아치지 않는다)
    void list.reduce((chain, f) => chain.then(() => upload(f)), Promise.resolve());
  };

  const remove = (f) => {
    setFiles(prev => {
      const rest = prev.filter(x => x.id !== f.id);
      onChange?.(rest);
      return rest;
    });
  };

  if (!uploadable && files.length === 0) return null;

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
        <Lbl>{label} {files.length > 0 && <span className="mono" style={{ color: 'var(--ink-3)' }}>{files.length}</span>}</Lbl>
        {uploadable && (
          <Button variant="secondary" size="sm" onClick={pick} disabled={busy}>
            {busy ? '올리는 중…' : '파일 추가'}
          </Button>
        )}
      </div>
      <input ref={inputRef} type="file" multiple onChange={onPicked} style={{ display: 'none' }} />
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        {files.map(f => (
          <FileRow key={f.id} file={f} busy={busy}
            onRemove={uploadable && !target ? remove : undefined} />
        ))}
        {files.length === 0 && uploadable && (
          <div style={{ padding: '10px 9px', border: '1px dashed var(--line)', borderRadius: 'var(--r-sm)',
            fontSize: 12, color: 'var(--ink-3)', textAlign: 'center' }}>
            첨부된 파일이 없습니다 (최대 50MB)
          </div>
        )}
      </div>
      {error && <div style={{ marginTop: 7, fontSize: 11.5, color: 'var(--crit)' }}>{error}</div>}
    </div>
  );
}
